/** Organizational timezone per product spec (Central Time). */
export const ORGANIZATION_TIME_ZONE = 'America/Chicago';

/**
 * Calendar date YYYY-MM-DD for an instant interpreted in the org timezone.
 * @param {Date} date
 * @returns {string}
 */
export function ymdInOrgTimeZone(date) {
  const d = date instanceof Date ? date : new Date(date);
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: ORGANIZATION_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = fmt.formatToParts(d);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  if (!y || !m || !day) return '';
  return `${y}-${m}-${day}`;
}

export function ymInOrgTimeZone(date = new Date()) {
  const ymd = ymdInOrgTimeZone(date);
  return ymd ? ymd.slice(0, 7) : '';
}

export function previousYm(ym) {
  const [year, month] = String(ym || '').split('-').map(Number);
  if (!year || !month) return '';
  const previous = new Date(Date.UTC(year, month - 2, 1));
  return `${previous.getUTCFullYear()}-${String(previous.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * First calendar day of the month (Central) containing `date`.
 * @param {Date} date
 * @returns {string} YYYY-MM-DD
 */
export function firstDayOfMonthYmdContaining(date) {
  const ymd = ymdInOrgTimeZone(date);
  const [y, m] = ymd.split('-');
  if (!y || !m) return '';
  return `${y}-${m}-01`;
}

export function todayYmdInOrgTimeZone() {
  return ymdInOrgTimeZone(new Date());
}

/**
 * Hour of day (0-23) for an instant interpreted in the org timezone.
 * @param {Date} date
 * @returns {number}
 */
export function hourInOrgTimeZone(date) {
  const d = date instanceof Date ? date : new Date(date);
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: ORGANIZATION_TIME_ZONE,
    hour: '2-digit',
    hour12: false,
  });
  const h = Number(fmt.formatToParts(d).find((p) => p.type === 'hour')?.value);
  // Intl may emit "24" for midnight in some runtimes; normalize to 0-23.
  return Number.isFinite(h) ? h % 24 : 0;
}

/** End-of-shift recap is available from 4:00 PM through the Central calendar day. */
export function isEndOfShiftRecapTime(date = new Date()) {
  return hourInOrgTimeZone(date) >= 16;
}

/**
 * 4-hour time-of-day bin index (0-5) for an instant in the org timezone.
 * 0 = 12:00-3:59am, 1 = 4-7:59am, ... 5 = 8-11:59pm.
 * @param {Date} date
 * @returns {number}
 */
export function fourHourBinIndex(date) {
  return Math.floor(hourInOrgTimeZone(date) / 4);
}

/**
 * Current time as "HH:MM" (24-hour) in the org timezone.
 * @returns {string}
 */
export function nowHmInOrgTimeZone() {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: ORGANIZATION_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date());
  const h = parts.find((p) => p.type === 'hour')?.value || '00';
  const m = parts.find((p) => p.type === 'minute')?.value || '00';
  return `${h}:${m}`;
}

/** @param {string} ymd */
export function formatYmdUs(ymd) {
  if (!ymd || typeof ymd !== 'string') return '';
  const [y, m, d] = ymd.split('-');
  if (!y || !m || !d) return ymd;
  return `${m}/${d}/${y}`;
}

/**
 * @param {object} args
 * @param {string} args.effectiveYmd - YYYY-MM-DD
 * @param {Date} args.entryAtApprox - proxy for server entryAt (e.g. click time)
 * @param {boolean} args.isAdmin
 * @returns {{ ok: boolean, error?: string, warn?: boolean, warnMessage?: string }}
 */
export function validateEffectiveDateForEncounter({ effectiveYmd, entryAtApprox, isAdmin }) {
  const todayYmd = todayYmdInOrgTimeZone();
  if (effectiveYmd > todayYmd) {
    return { ok: false, error: 'Effective date cannot be in the future.' };
  }
  const floorYmd = firstDayOfMonthYmdContaining(entryAtApprox);
  if (!isAdmin && effectiveYmd < floorYmd) {
    return {
      ok: false,
      error: `Only admins can date an encounter before ${formatYmdUs(floorYmd)} (first day of the current month, Central Time). Choose a date in this month, or contact an admin to change the date.`,
    };
  }
  if (isAdmin && effectiveYmd < floorYmd) {
    return {
      ok: true,
      warn: true,
      warnMessage: `Effective date is before ${formatYmdUs(floorYmd)} (first day of the entry month, Central). Continue?`,
    };
  }
  return { ok: true, warn: false };
}
